import { hasSceneSeatPermission, hasXgSeatPermission } from "@utils/permission-utils";
import { isActiveMember } from "@utils/xg-seat-utils";

/** The seats of the workspace subscription that a member can hold */

/** The name of each seat, as the user knows the product it unlocks */
export const MEMBER_SEAT_LABELS = {
  xgSeat: "Sphere XG",
  sceneSeat: "SCENE"
};

/** What each seat is required for, so that the user knows what they are handing out */
export const MEMBER_SEAT_DESCRIPTIONS = {
  xgSeat: "Required for admins and editors",
  sceneSeat: "Required to use SCENE"
};

/**
 * What it takes to tell which seats a member holds: which seats the plan includes, and the active
 * members, who hold a Sphere XG seat because of their roles.
 */

/** A lookup in which nobody holds a seat, for the tables that do not show the seats */
export const EMPTY_MEMBER_SEATS_LOOKUP = {
  activeMemberKeys: new Set(),
  isActiveSeatPlan: false,
  isUserRolePlan: false,
  hasSceneSeats: false
};

/** Whether the plan of the workspace includes any seat its members can hold */
export function doesPlanIncludeSeats({
  isActiveSeatPlan,
  isUserRolePlan,
  hasSceneSeats
}) {
  return isActiveSeatPlan || isUserRolePlan || hasSceneSeats;
}

/** The seats a member holds */

/** A seat a member can hold, including the Sphere XG seat that comes with being an active user */

/** How each held seat is named in a sentence */
const HELD_SEAT_NAMES = {
  xgSeat: `a ${MEMBER_SEAT_LABELS.xgSeat} seat`,
  xgActiveUserSeat: `a ${MEMBER_SEAT_LABELS.xgSeat} seat`,
  sceneSeat: `a ${MEMBER_SEAT_LABELS.sceneSeat} seat`
};

/**
 * Returns which seats a member holds. A seat only counts when the plan includes it. A SCENE seat is
 * the `sceneSeat` permission of the member, and teams have no permissions, so they hold none.
 *
 * @param member The member to check
 * @param lookup The seats of the workspace
 */
export function getHeldSeats(member, lookup) {
  return {
    hasXgSeat: lookup.isActiveSeatPlan && hasXgSeatPermission({
      member
    }),
    hasXgActiveUserSeat: lookup.isUserRolePlan && isActiveMember(member, lookup.activeMemberKeys),
    hasSceneSeat: lookup.hasSceneSeats && hasSceneSeatPermission({
      member
    })
  };
}

/** Whether a member holds a Sphere XG seat of either kind */
export function hasAnyXgSeat(heldSeats) {
  return heldSeats.hasXgSeat || heldSeats.hasXgActiveUserSeat;
}

/**
 * Returns the seats the plan of the workspace includes, in the order they are shown. Both kinds of
 * Sphere XG seat look the same to the user, so either plan constraint lists a single one.
 */
export function getPlanSeats({
  isActiveSeatPlan,
  isUserRolePlan,
  hasSceneSeats
}) {
  const seats = [];
  if (isActiveSeatPlan || isUserRolePlan) {
    seats.push("xgSeat");
  }
  if (hasSceneSeats) {
    seats.push("sceneSeat");
  }
  return seats;
}

/** Whether a member holds the seat, counting a Sphere XG seat of either kind */
export function isSeatHeld(heldSeats, seat) {
  return seat === "xgSeat" ? hasAnyXgSeat(heldSeats) : heldSeats.hasSceneSeat;
}

/**
 * Whether it is known if a member holds the seat. A seat assigned to them is known from their
 * permissions, but the Sphere XG seat of an active user only from the active members, which only
 * the users that may view the subscription details can read.
 *
 * @param heldSeats The seats the member holds
 * @param seat The seat to tell
 * @param lookup The seats of the workspace
 * @param areActiveMembersKnown Whether the stored active members of the workspace are current, see
 * areActiveMembersCurrentSelector
 */
export function isSeatHoldingKnown(heldSeats, seat, {
  isUserRolePlan
}, areActiveMembersKnown) {
  return seat !== "xgSeat" || !isUserRolePlan || heldSeats.hasXgSeat || areActiveMembersKnown;
}

/**
 * Returns how much the seats a member holds weigh when sorting by them: a Sphere XG and a SCENE
 * seat rank highest, then Sphere XG only, then SCENE only and no seat at all lowest.
 */
export function getSeatsRank(heldSeats) {
  // Sphere XG counts twice, so that it outweighs SCENE but not both seats together
  return Number(hasAnyXgSeat(heldSeats)) * 2 + Number(heldSeats.hasSceneSeat);
}

/**
 * Returns the seats a member holds, in the order they are shown. Both kinds of Sphere XG seat look
 * the same to the user, so a member holding both is shown with a single one.
 */
export function getHeldSeatList(heldSeats) {
  const seats = [];
  if (heldSeats.hasXgSeat) {
    seats.push("xgSeat");
  } else if (heldSeats.hasXgActiveUserSeat) {
    seats.push("xgActiveUserSeat");
  }
  if (heldSeats.hasSceneSeat) {
    seats.push("sceneSeat");
  }
  return seats;
}

/**
 * Returns what to tell the user about the seats a member holds, or undefined when they hold none,
 * e.g. "Holds a Sphere XG seat and a SCENE seat".
 */
export function getHeldSeatsDescription(heldSeats) {
  const names = getHeldSeatList(heldSeats).map(seat => HELD_SEAT_NAMES[seat]);
  if (!names.length) {
    return undefined;
  }
  const last = names.pop();
  return names.length ? `Holds ${names.join(", ")} and ${last}` : `Holds ${last}`;
}

/** Returns what to tell the user about a member holding a single seat */
export function getHeldSeatDescription(seat) {
  return `Holds ${HELD_SEAT_NAMES[seat]}`;
}